import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { DataGrid, GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import {
  Alert,
  AlertColor,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  Link,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/KeyOutlined';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/AlternateEmailOutlined';
import LockResetIcon from '@mui/icons-material/LockResetOutlined';
import BlockIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import SendIcon from '@mui/icons-material/SendOutlined';
import { PageHeader } from '../../components/PageHeader';
import {
  changePortalLoginEmail,
  invitePortalAccount,
  listPortalAccounts,
  sendPortalPasswordReset,
  setPortalAccountEnabled,
} from '../../api/portalAccounts';
import { EmailDelivery, PortalAccountRow, PortalAccountStatus } from '../../types/portalAccount';

const errorMessage = (err: unknown) =>
  isAxiosError(err) ? (err.response?.data?.message ?? 'Something went wrong.') : 'Something went wrong.';

const STATUS: Record<PortalAccountStatus, { label: string; color: 'success' | 'error' | 'default' }> = {
  ACTIVE: { label: 'Active', color: 'success' },
  DISABLED: { label: 'Disabled', color: 'error' },
  NOT_CREATED: { label: 'Not created', color: 'default' },
};

const personName = (p: { firstName: string; lastName: string } | null) => (p ? `${p.firstName} ${p.lastName}` : '');
const formatDateTime = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Action = 'email' | 'reset' | 'disable' | 'enable' | 'invite';
type Toast = { severity: AlertColor; message: string };

const deliveryNote = (delivery: EmailDelivery, address: string) =>
  delivery === 'SENT'
    ? null
    : delivery === 'NOT_CONFIGURED'
      ? `no email could be sent to ${address} (email isn't configured on this server)`
      : `the email to ${address} failed`;

/** Administration → Student Logins (Phase 100, Super Admin only — the API
 * refuses everyone else). Each student's Student Portal login: status, login
 * email, last login and who last enabled/disabled it, with Edit email, Send
 * password reset, Enable/Disable and Invite/Resend. Passwords are never
 * shown or set here; a reset only emails the student a single-use link. */
export const StudentPortalLoginsPage = () => {
  const queryClient = useQueryClient();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<PortalAccountStatus | ''>('');
  const [dialog, setDialog] = useState<{ action: Action; row: PortalAccountRow } | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [reason, setReason] = useState('');
  const [toast, setToast] = useState<Toast | null>(null);

  const params = { search: search.trim() || undefined, status: status || undefined };
  const listQuery = useQuery({
    queryKey: ['portal-accounts', paginationModel, params],
    queryFn: () =>
      listPortalAccounts({ ...params, page: paginationModel.page + 1, pageSize: paginationModel.pageSize }),
    placeholderData: (prev) => prev,
  });

  const open = (action: Action, row: PortalAccountRow) => {
    setNewEmail('');
    setConfirmEmail('');
    setReason('');
    mutation.reset();
    setDialog({ action, row });
  };

  const mutation = useMutation({
    mutationFn: async ({ action, row }: { action: Action; row: PortalAccountRow }): Promise<Toast> => {
      const name = `${row.firstName} ${row.lastName}`;
      if (action === 'reset') {
        const result = await sendPortalPasswordReset(row.studentId);
        if (result.delivery === 'SENT') {
          return {
            severity: 'success',
            message: `Password reset email sent to ${row.account!.loginEmail}. The link works once and expires in 1 hour.`,
          };
        }
        return {
          severity: 'error',
          message:
            result.delivery === 'NOT_CONFIGURED'
              ? "Email isn't configured on this server, so no password reset email was sent."
              : `The password reset email couldn't be sent${result.error ? ` (${result.error})` : ''}. No link was issued.`,
        };
      }
      if (action === 'email') {
        const result = await changePortalLoginEmail(row.studentId, newEmail.trim(), confirmEmail.trim());
        const notes = [
          deliveryNote(result.notified.previousAddress, row.account!.loginEmail),
          deliveryNote(result.notified.newAddress, newEmail.trim()),
        ].filter(Boolean);
        return {
          severity: notes.length ? 'warning' : 'success',
          message: `Login email for ${name} changed to ${newEmail.trim()}. They have been signed out.${
            notes.length ? ` Note: ${notes.join('; ')}.` : ' Both addresses were told.'
          }`,
        };
      }
      if (action === 'invite') {
        const { invite } = await invitePortalAccount(row.studentId);
        return invite.status === 'SENT'
          ? { severity: 'success', message: `Invitation sent to ${name}.` }
          : {
              severity: 'warning',
              message: `The portal login is ready, but the invitation email wasn't sent${
                invite.errorMessage ? `: ${invite.errorMessage}` : '.'
              }`,
            };
      }
      const enabled = action === 'enable';
      await setPortalAccountEnabled(row.studentId, enabled, reason.trim() || undefined);
      return {
        severity: 'success',
        message: enabled
          ? `Portal login enabled for ${name}.`
          : `Portal login disabled for ${name}. They have been signed out.`,
      };
    },
    onSuccess: (result) => {
      setToast(result);
      setDialog(null);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['portal-accounts'] }),
  });

  const columns: GridColDef<PortalAccountRow>[] = [
    {
      field: 'student',
      headerName: 'Student',
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
          <Link component={RouterLink} to={`/students/${row.studentId}`}>
            {row.firstName} {row.lastName}
          </Link>
          <Typography variant="caption" color="text.secondary" display="block">
            {row.studentNumber} · {row.registrationNumber}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'loginEmail',
      headerName: 'Login email',
      flex: 1,
      minWidth: 220,
      sortable: false,
      valueGetter: (_v, row) => row.account?.loginEmail ?? '—',
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 190,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
          <Chip size="small" label={STATUS[row.status].label} color={STATUS[row.status].color} />
          {row.account && !row.account.passwordSet && row.status === 'ACTIVE' && (
            <Typography variant="caption" color="text.secondary" display="block">
              Invitation not yet accepted
            </Typography>
          )}
          {row.statusChangedBy && row.statusChangedAt && (
            <Typography variant="caption" color="text.secondary" display="block">
              {row.status === 'DISABLED' ? 'Disabled' : 'Enabled'} by {personName(row.statusChangedBy)},{' '}
              {formatDateTime(row.statusChangedAt)}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      field: 'lastLoginAt',
      headerName: 'Last login',
      width: 170,
      sortable: false,
      valueGetter: (_v, row) =>
        !row.account ? '—' : row.account.lastLoginAt ? formatDateTime(row.account.lastLoginAt) : 'Never',
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      sortable: false,
      renderCell: ({ row }) => {
        const name = `${row.firstName} ${row.lastName}`;
        const action = (label: string, icon: React.ReactNode, onClick: () => void, color?: 'error' | 'success') => (
          <Tooltip title={label}>
            <IconButton size="small" color={color} aria-label={`${label} — ${name}`} onClick={onClick}>
              {icon}
            </IconButton>
          </Tooltip>
        );
        return (
          <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
            {row.status !== 'DISABLED' &&
              action(
                row.status === 'NOT_CREATED' ? 'Send invitation' : 'Resend invitation',
                <SendIcon fontSize="small" />,
                () => open('invite', row),
              )}
            {row.account && action('Edit login email', <EditIcon fontSize="small" />, () => open('email', row))}
            {row.status === 'ACTIVE' &&
              action('Send password reset', <LockResetIcon fontSize="small" />, () => open('reset', row))}
            {row.status === 'ACTIVE' &&
              action('Disable login', <BlockIcon fontSize="small" />, () => open('disable', row), 'error')}
            {row.status === 'DISABLED' &&
              action('Enable login', <CheckCircleIcon fontSize="small" />, () => open('enable', row), 'success')}
          </Stack>
        );
      },
    },
  ];

  const row = dialog?.row;
  const name = row ? `${row.firstName} ${row.lastName}` : '';
  const emailValid = EMAIL_PATTERN.test(newEmail.trim());
  const emailsMatch = newEmail.trim().toLowerCase() === confirmEmail.trim().toLowerCase();
  const canSubmit =
    dialog?.action !== 'email' || (emailValid && emailsMatch && newEmail.trim().length > 0);

  const DIALOG: Record<Action, { title: string; confirm: string; color: 'primary' | 'error' }> = {
    email: { title: 'Change login email', confirm: 'Change login email', color: 'primary' },
    reset: { title: 'Send password reset', confirm: 'Send reset email', color: 'primary' },
    disable: { title: 'Disable portal login', confirm: 'Disable login', color: 'error' },
    enable: { title: 'Enable portal login', confirm: 'Enable login', color: 'primary' },
    invite: { title: 'Send portal invitation', confirm: 'Send invitation', color: 'primary' },
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        icon={KeyIcon}
        color="#7A5AF8"
        title="Student Logins"
        subtitle="Student Portal accounts: status, login email and last login. Passwords are never shown — a reset emails the student a single-use link."
      />
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search name, student #, registration # or email…"
          sx={{ width: { xs: '100%', sm: 360 } }}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPaginationModel((p) => ({ ...p, page: 0 }));
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          label="Status"
          sx={{ width: 180 }}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as PortalAccountStatus | '');
            setPaginationModel((p) => ({ ...p, page: 0 }));
          }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="DISABLED">Disabled</MenuItem>
          <MenuItem value="NOT_CREATED">Not created</MenuItem>
        </TextField>
      </Stack>

      {listQuery.isError ? (
        <Alert severity="error">{errorMessage(listQuery.error)}</Alert>
      ) : (
        <Box sx={{ height: 640, bgcolor: 'background.paper', borderRadius: 1 }}>
          <DataGrid
            rows={listQuery.data?.data ?? []}
            getRowId={(r) => r.studentId}
            columns={columns}
            rowCount={listQuery.data?.meta.total ?? 0}
            loading={listQuery.isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 20, 50, 100]}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            localeText={{ noRowsLabel: 'No students match.' }}
          />
        </Box>
      )}

      <Dialog open={!!dialog} onClose={() => !mutation.isPending && setDialog(null)} maxWidth="sm" fullWidth>
        {dialog && row && (
          <>
            <DialogTitle>
              {DIALOG[dialog.action].title} — {name}
            </DialogTitle>
            <DialogContent>
              <Stack spacing={2} sx={{ mt: 0.5 }}>
                {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
                {dialog.action === 'email' && (
                  <>
                    <DialogContentText>
                      Current login email: <strong>{row.account?.loginEmail}</strong>. The new address must not
                      be used by any other account or student. The student will be signed out, unused reset
                      links will stop working, and both addresses will be told. Their password and contact email
                      stay the same.
                    </DialogContentText>
                    <TextField
                      label="New login email"
                      type="email"
                      autoFocus
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      error={!!newEmail && !emailValid}
                      helperText={newEmail && !emailValid ? 'Enter a valid email address' : ' '}
                    />
                    <TextField
                      label="Confirm new login email"
                      type="email"
                      value={confirmEmail}
                      onChange={(e) => setConfirmEmail(e.target.value)}
                      error={!!confirmEmail && !emailsMatch}
                      helperText={confirmEmail && !emailsMatch ? "The two addresses don't match" : ' '}
                    />
                  </>
                )}
                {dialog.action === 'reset' && (
                  <DialogContentText>
                    Email a password reset link to <strong>{row.account?.loginEmail}</strong>? The link works once
                    and expires in 1 hour; any earlier link stops working. Their current password keeps working
                    until they use it.
                  </DialogContentText>
                )}
                {dialog.action === 'disable' && (
                  <>
                    <DialogContentText>
                      {name} won't be able to sign in to the Student Portal, and any open session ends now.
                      Their student record, payments, documents, invoices and history are not changed, and you
                      can enable the login again at any time.
                    </DialogContentText>
                    <TextField
                      label="Reason (optional)"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      inputProps={{ maxLength: 500 }}
                    />
                  </>
                )}
                {dialog.action === 'enable' && (
                  <DialogContentText>
                    {name} will be able to sign in again with the same login email ({row.account?.loginEmail}) and
                    password.
                  </DialogContentText>
                )}
                {dialog.action === 'invite' && (
                  <DialogContentText>
                    {row.status === 'NOT_CREATED'
                      ? `Create a Student Portal login for ${name} with ${row.contactEmail ?? 'their email (none on file)'} and email them an invitation to set a password?`
                      : `Email ${row.account?.loginEmail} a new invitation link? Any earlier link stops working.`}
                  </DialogContentText>
                )}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialog(null)} disabled={mutation.isPending}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color={DIALOG[dialog.action].color}
                disabled={!canSubmit || mutation.isPending}
                onClick={() => mutation.mutate({ action: dialog.action, row })}
              >
                {DIALOG[dialog.action].confirm}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={8000} onClose={() => setToast(null)}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};
